import React, { useState, useEffect } from 'react';

const LocationSelector = ({ userLocation, setUserLocation, userCoordinates, setUserCoordinates, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLocating, setIsLocating] = useState(false);
    const [distance, setDistance] = useState(null);

    // Hardcoded Store Location
    const STORE_LAT = 19.100378;
    const STORE_LNG = 73.0015257;

    // Haversine formula to calculate distance in km
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of the earth in km
        const dLat = (lat2 - lat1) * Math.PI / 180;  // deg2rad below
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2); 
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        const d = R * c; // Distance in km
        return d;
    };

    useEffect(() => {
        if (userCoordinates && userCoordinates.lat && userCoordinates.lng) {
            const dist = calculateDistance(STORE_LAT, STORE_LNG, userCoordinates.lat, userCoordinates.lng);
            setDistance(dist.toFixed(1));
        } else {
            setDistance(null);
        }
    }, [userCoordinates]);

    useEffect(() => {
        // Load initial from local storage if available
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation && !userLocation) {
            setUserLocation(savedLocation);
        }
    }, [setUserLocation, userLocation]);

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (inputValue.trim()) {
            const loc = inputValue.trim();
            
            // Try to geocode the manual input to get coordinates and formatted address
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(loc)}&format=json&limit=1&addressdetails=1&countrycodes=in`);
                const data = await res.json();
                if (data && data.length > 0) {
                    const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
                    if (setUserCoordinates) {
                        setUserCoordinates(coords);
                        localStorage.setItem('userCoordinates', JSON.stringify(coords));
                    }
                    
                    // Extract best location (area and pincode)
                    const address = data[0].address || {};
                    const area = address.suburb || address.village || address.city || address.town || address.county || data[0].name || loc;
                    const pincode = address.postcode || "";
                    const locationString = [area, pincode].filter(Boolean).join(" - ");
                    
                    setUserLocation(locationString);
                    localStorage.setItem('userLocation', locationString);
                    setInputValue(locationString);
                } else {
                    // Fallback to what user typed if not found
                    setUserLocation(loc);
                    localStorage.setItem('userLocation', loc);
                }
            } catch (err) {
                console.error("Geocoding manual input failed:", err);
                setUserLocation(loc);
                localStorage.setItem('userLocation', loc);
            }

            setIsOpen(false);
        }
    };

    const handleAutoLocate = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser.");
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            try {
                const { latitude, longitude } = position.coords;
                // Using OpenStreetMap Nominatim for free reverse geocoding
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                const data = await res.json();
                
                // Extract best location (area and pincode)
                const address = data.address;
                const area = address.suburb || address.village || address.city || address.town || "";
                const pincode = address.postcode || "";
                const locationString = [area, pincode].filter(Boolean).join(" - ") || "Unknown Location";
                
                setInputValue(locationString);
                setUserLocation(locationString);
                localStorage.setItem('userLocation', locationString);

                const coords = { lat: latitude, lng: longitude };
                if (setUserCoordinates) {
                    setUserCoordinates(coords);
                    localStorage.setItem('userCoordinates', JSON.stringify(coords));
                }

                setIsOpen(false);
            } catch (error) {
                console.error("Error auto-locating", error);
                alert("Could not detect your location. Please enter manually.");
            } finally {
                setIsLocating(false);
            }
        }, (error) => {
            setIsLocating(false);
            console.error("Geolocation error", error);
            alert("Permission denied or location unavailable.");
        });
    };

    return (
        <div className="relative z-50 flex items-stretch">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-3 sm:py-0 hover:bg-slate-50 transition-colors border-r border-slate-200 focus:outline-none h-full min-h-[48px] ${className}`}
            >
                <i className="fas fa-map-marker-alt text-sky-500 text-lg"></i>
                <div className="flex flex-col items-start justify-center">
                    <span className="text-xs font-bold text-slate-700 truncate max-w-[100px] md:max-w-[150px]">
                        {userLocation ? userLocation.split(' - ')[0] : 'Set Location'}
                    </span>
                    {userLocation && userLocation.includes(' - ') && (
                        <span className="text-[10px] font-bold text-slate-500 truncate max-w-[100px] md:max-w-[150px] leading-tight">
                            {userLocation.split(' - ')[1]}
                        </span>
                    )}
                    {distance && (
                        <span className="text-[10px] text-sky-600 font-semibold truncate max-w-[100px] md:max-w-[150px] leading-tight">
                            {distance} km away
                        </span>
                    )}
                </div>
                <i className={`fas fa-chevron-down text-[10px] text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 z-50">
                    <h4 className="text-sm font-extrabold text-slate-700 mb-2 flex items-center gap-2">
                        <i className="fas fa-location-arrow text-sky-500"></i> Delivery Location
                    </h4>
                    <p className="text-xs text-slate-500 mb-4 font-medium">Enter your pincode/location or use auto-detect for accurate delivery options.</p>
                    
                    <div className="mb-4 relative">
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(e); }}
                            placeholder="Enter Pincode or Location"
                            className="w-full pl-3 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white transition-colors"
                        />
                        <button type="button" onClick={handleSave} className="absolute right-2 top-1/2 -translate-y-1/2 text-sky-500 hover:text-sky-600 p-1">
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px bg-slate-200 flex-1"></div>
                        <span className="text-xs text-slate-400 font-bold uppercase">OR</span>
                        <div className="h-px bg-slate-200 flex-1"></div>
                    </div>

                    <button
                        onClick={handleAutoLocate}
                        disabled={isLocating}
                        type="button"
                        className="w-full flex items-center justify-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors"
                    >
                        {isLocating ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fas fa-crosshairs"></i>
                        )}
                        {isLocating ? 'Detecting...' : 'Auto Detect Location'}
                    </button>
                </div>
            )}
            
            {isOpen && (
                <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsOpen(false)}
                ></div>
            )}
        </div>
    );
};

export default LocationSelector;
